import React, { useCallback, useContext, useMemo, useState } from "react";
import { Input, InputProps } from "@chakra-ui/react";
import { Controller, Control, RegisterOptions } from "react-hook-form";
import { AuthContext } from "@/providers/auth";

/**
 * Props for JailTextInputWrapper. This component is meant to be used with
 * react-hook-form. Supply your field's name, the control object, any validation
 * rules, and (optionally) set maskInputField to true.
 */
export interface JailTextInputWrapperProps
  extends Omit<InputProps, "value" | "onChange" | "name" | "ref"> {
  /** Field name as defined in your react-hook-form data */
  name: string;
  /** The control object from useForm() */
  control: Control<any>;
  /** Validation rules for react-hook-form */
  rules?: RegisterOptions;
  /**
   * If true, when the input loses focus, it will display only the first 3 characters
   * with the remainder masked as asterisks (up to 8 characters total). While editing,
   * the input is cleared so the previous value is never visible.
   */
  maskInputField?: boolean;
}

/**
 * JailTextInputWrapper is a Chakra UI Input component with built-in masking
 * behavior. It’s wired up to react-hook-form using Controller, so you only need
 * to provide the field name, control, and any rules.
 *
 * Usage:
 *
 * ```tsx
 * <FormControl isInvalid={!!errors.FirstName}>
 *   <FormLabel>First Name</FormLabel>
 *   <JailTextInputWrapper
 *     name="FirstName"
 *     control={control}
 *     rules={{ required: "This field is required" }}
 *     placeholder="John"
 *     maskInputField={true}
 *   />
 *   {errors.FirstName && <FormErrorMessage>{errors.FirstName.message}</FormErrorMessage>}
 * </FormControl>
 * ```
 */
export const InputWrapper: React.FC<JailTextInputWrapperProps> = ({
  name,
  control,
  rules,
  ...rest
}) => {
  const { userDetails } = useContext(AuthContext);
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <MaskedInput
          field={field}
          maskInputField={
            (userDetails || {}).isJailUser && !(userDetails || {}).isJailSso
          }
          {...rest}
        />
      )}
    />
  );
};

/**
 * Internal component that contains the masking logic and renders a Chakra UI <Input>.
 * This is used only inside JailTextInputWrapper.
 */
interface MaskedInputProps extends InputProps {
  maskInputField: boolean;
  field: {
    value: any;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    name: string;
    ref: React.Ref<any>;
  };
}

export function maskValue(value: string) {
  if (!value) return value;
  const firstPart = value.slice(0, 3);
  const maskedCount = 8; //Math.min(value.length - 3, 5);
  const maskedPart = "*".repeat(maskedCount);
  const masked = firstPart + maskedPart;
  return masked.length > 8 ? masked.slice(0, 8) : masked;
}
const MaskedInput: React.FC<MaskedInputProps> = ({
  field,
  maskInputField,
  ...rest
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  // Returns the masked version of the value: first 3 characters visible,
  // remaining characters replaced with '*' (up to 8 total characters).
  const maskedValue = useCallback((raw: string): string => {
    return maskValue(raw);
  }, []);

  // Determine what to display: the actual value, the local editing value, or the masked value.
  const displayedValue = useMemo(() => {
    if (!maskInputField) return field.value || "";
    if (isEditing) return editValue;
    return maskedValue(field.value || "");
  }, [maskInputField, isEditing, editValue, field.value, maskedValue]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (maskInputField) {
      setIsEditing(true);
      setEditValue("");
    }
    if (rest.onFocus) rest.onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (maskInputField) {
      setIsEditing(false);
      // If the user typed a non-empty value, update the form state with the new (unmasked) value.
      if (editValue.trim().length > 0) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: editValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        field.onChange(syntheticEvent);
      }
    }
    field.onBlur();
    if (rest.onBlur) rest.onBlur(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (maskInputField && isEditing) {
      setEditValue(e.target.value);
    } else {
      field.onChange(e);
    }
    if (rest.onChange) rest.onChange(e);
  };

  return (
    <Input
      {...rest}
      value={displayedValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      name={field.name}
      ref={field.ref}
    />
  );
};
